// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ChartCard
 * @package @geenius/analytics/react
 * @description Renders the React chart card used to visualize simple analytics
 * series such as trends, counts, and comparisons. The component converts shared
 * chart points into a lightweight bar-style presentation.
 */

import type { ChartDataPoint } from "@geenius/analytics";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tooltip,
} from "@geenius/ui/react";
import type React from "react";
import { cn } from "../lib/cn";

function getErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

/**
 * Props for rendering the React chart card.
 *
 * @property title Section heading displayed above the chart body.
 * @property data Ordered data points rendered as bars.
 * @property type Optional chart mode metadata for consumers.
 * @property height Optional pixel height for the chart body.
 * @property error Optional chart-level error message or object.
 * @property onRetry Optional retry handler rendered when an error is present.
 * @property className Optional class name merged into the card wrapper.
 */
export interface ChartCardProps {
  title: string;
  data: ChartDataPoint[];
  type?: "bar" | "line";
  height?: number;
  error?: Error | string | null;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders a React chart card for a list of analytics datapoints.
 *
 * @param props Chart metadata and datapoints to visualize.
 * @returns A chart card element with proportional bars.
 */
export function ChartCard(props: ChartCardProps): React.ReactElement {
  const max = Math.max(...props.data.map((point) => point.value), 1);

  if (props.error) {
    return (
      <Card className={props.className}>
        <CardHeader className="pb-0">
          <CardTitle className="text-sm">{props.title}</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-3" role="alert">
            <span className="text-sm text-muted-foreground">
              {getErrorMessage(props.error)}
            </span>
            {props.onRetry && (
              <Button
                className="w-fit"
                type="button"
                variant="secondary"
                onClick={props.onRetry}
              >
                Retry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={props.className}>
      <CardHeader className="pb-0">
        <CardTitle className="text-sm">{props.title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <div
          className="flex min-h-0 items-end gap-3"
          data-analytics="chart-body"
          data-chart-type={props.type ?? "bar"}
          style={{ height: props.height ?? 200 }}
        >
          <div
            className="flex h-full flex-1 items-end gap-3"
            data-analytics="chart-bars"
          >
            {props.data.map((point) => (
              <div
                key={`${point.label}-${point.value}`}
                className="flex min-w-0 flex-1 flex-col items-center gap-2"
                data-analytics="chart-bar-group"
              >
                <Tooltip content={`${point.label}: ${point.value}`}>
                  <div
                    className={cn(
                      "w-full rounded-t-md bg-primary/80 transition-opacity motion-reduce:transition-none",
                      props.type === "line" && "rounded-full bg-primary/60",
                    )}
                    data-analytics="chart-bar"
                    style={{ height: `${(point.value / max) * 100}%` }}
                  />
                </Tooltip>
                <span
                  className="line-clamp-2 text-center text-xs text-muted-foreground"
                  data-analytics="chart-label"
                >
                  {point.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
